import React from 'react';
import { VehicleType } from '../../types';
import { useMasterData } from '../../hooks/useMasterData';
import {
  XMarkIcon,
  TagIcon,
  TruckIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui';

interface VehicleTypeDetailProps {
  vehicleType: VehicleType;
  onClose: () => void;
  onDelete?: (typeId: string, typeName: string) => void;
  isModal?: boolean;
}

export const VehicleTypeDetail: React.FC<VehicleTypeDetailProps> = ({
  vehicleType,
  onClose,
  onDelete,
  isModal = true,
}) => {
  const { vehicleModels, landTransporters } = useMasterData();

  // 使用中の車両モデル一覧
  const usedInModels = vehicleModels.filter(model => model.vehicleTypeId === vehicleType.id);

  // 使用中の陸送員一覧
  const usedInTransporters = landTransporters.filter(
    transporter =>
      transporter.drivableVehicleTypes && transporter.drivableVehicleTypes.includes(vehicleType.id)
  );

  const isInUse = usedInModels.length > 0 || usedInTransporters.length > 0;

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(vehicleType.id, vehicleType.name);
    }
  };

  const DetailContent = () => (
    <div className='space-y-6'>
      {/* ヘッダー */}
      <div className='flex items-start justify-between'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center space-x-3 mb-2'>
            <TagIcon className='h-6 w-6 text-blue-600 dark:text-blue-400' />
            <h2 className='text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate'>
              {vehicleType.name}
            </h2>
          </div>
          <p className='text-sm text-gray-500 dark:text-gray-400'>ID: {vehicleType.id}</p>
        </div>
        {isModal && (
          <button
            onClick={onClose}
            className='p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700'
            aria-label='詳細を閉じる'
          >
            <XMarkIcon className='h-5 w-5' />
          </button>
        )}
      </div>

      {/* 使用状況サマリー */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
          <ClipboardDocumentListIcon className='h-5 w-5 mr-2' />
          使用状況
        </h3>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1'>
              車両モデル
            </label>
            <p className='text-2xl font-bold text-gray-900 dark:text-white'>
              {usedInModels.length}
              <span className='text-sm font-normal text-gray-500 dark:text-gray-400 ml-1'>件</span>
            </p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1'>
              陸送員
            </label>
            <p className='text-2xl font-bold text-gray-900 dark:text-white'>
              {usedInTransporters.length}
              <span className='text-sm font-normal text-gray-500 dark:text-gray-400 ml-1'>名</span>
            </p>
          </div>
        </div>

        {!isInUse && (
          <div className='flex items-center'>
            <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'>
              未使用
            </span>
            <span className='ml-2 text-xs text-gray-500 dark:text-gray-400'>
              この車種は現在使用されていません
            </span>
          </div>
        )}
      </div>

      {/* 使用中の車両モデル一覧 */}
      {usedInModels.length > 0 && (
        <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
            <TruckIcon className='h-5 w-5 mr-2' />
            使用中の車両モデル ({usedInModels.length}件)
          </h3>

          <div className='space-y-2 max-h-60 overflow-y-auto'>
            {usedInModels.map(model => (
              <div
                key={model.id}
                className='flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg'
              >
                <div>
                  <p className='text-sm font-medium text-gray-900 dark:text-white'>{model.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 使用中の陸送員一覧 */}
      {usedInTransporters.length > 0 && (
        <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
            <UserGroupIcon className='h-5 w-5 mr-2' />
            使用中の陸送員 ({usedInTransporters.length}名)
          </h3>

          <div className='space-y-2 max-h-60 overflow-y-auto'>
            {usedInTransporters.map(transporter => (
              <div
                key={transporter.id}
                className='flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg'
              >
                <div>
                  <p className='text-sm font-medium text-gray-900 dark:text-white'>
                    {transporter.name}
                  </p>
                  <p className='text-xs text-gray-500 dark:text-gray-400'>ID: {transporter.id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 削除警告 */}
      {isInUse && (
        <div className='p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md'>
          <p className='text-xs text-yellow-800 dark:text-yellow-200'>
            ⚠️
            この車種は使用中のため削除できません。削除するには、関連する車両モデルや陸送員から該当の車種を削除してください。
          </p>
        </div>
      )}

      {/* アクション */}
      <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600'>
        {/* 削除ボタン（未使用の場合のみ表示） */}
        {!isInUse && onDelete && (
          <Button onClick={handleDeleteClick} variant='danger' size='md'>
            <TrashIcon className='h-4 w-4 mr-2' />
            削除
          </Button>
        )}

        {!isModal ? (
          <Button onClick={onClose} variant='secondary' size='md'>
            戻る
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
          <div className='p-6'>
            <DetailContent />
          </div>
        </div>
      </div>
    );
  }

  return <DetailContent />;
};
